// Library mode for the animation gallery: load any animated GLB (the shared
// CC0 library's models), enumerate its own clips and play them with the same
// scene chrome as the prototype-rig gallery. No retargeting - library clips
// play on library models only; the prototype flow is untouched.
// `clip` is optional: omitting it puts the stage in static mode (load and
// display the mesh, no mixer playback) - used by the Animation Studio to
// preview a project's own uploaded, unrigged GLB assets with this same
// scene chrome rather than duplicating it.
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';

import { disposeProp } from './GalleryStage';
import { Viewport } from '../../runtime/viewport/Viewport';

export interface LibraryGalleryProps {
  /** GLB URL (a library blob, or a project's own uploaded asset). */
  url: string;
  /** Exact clip name from the model's own animation list. Omit for static
   * mode: the mesh loads and displays with no animation playback. */
  clip?: string;
  /** Playback rate. Default 1. */
  speed?: number;
  /** Freeze the mixer without unmounting. */
  paused?: boolean;
  /** Scene clear colour. Defaults to the gallery's near-black. */
  background?: string;
  style?: CSSProperties;
  className?: string;
  /** Called with the model's clip names once loaded. */
  onClips?: (clips: string[]) => void;
}

/** Ground the model at y=0 and scale oversized or tiny models towards a
 * stage-friendly height; the camera and grid assume roughly human scale. */
function normalise(root: THREE.Object3D): void {
  root.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(root);
  const height = box.max.y - box.min.y;
  if (height > 0 && (height > 3 || height < 0.5)) {
    root.scale.setScalar(1.8 / height);
  }
  root.updateMatrixWorld(true);
  const grounded = new THREE.Box3().setFromObject(root);
  root.position.y -= grounded.min.y;
  root.position.x -= (grounded.min.x + grounded.max.x) / 2;
  root.position.z -= (grounded.min.z + grounded.max.z) / 2;
}

function LibraryRig({ url, clip, speed = 1, paused = false, onClips }: LibraryGalleryProps) {
  const [model, setModel] = useState<{ gltf: GLTF; mixer: THREE.AnimationMixer } | null>(null);
  const actionRef = useRef<THREE.AnimationAction | null>(null);

  useEffect(() => {
    let cancelled = false;
    let mixer: THREE.AnimationMixer | null = null;
    let loadedScene: THREE.Object3D | null = null;
    new GLTFLoader().load(
      url,
      (gltf) => {
        if (cancelled) return;
        loadedScene = gltf.scene;
        normalise(gltf.scene);
        gltf.scene.traverse((node) => {
          const mesh = node as THREE.Mesh;
          if (!mesh.isMesh) return;
          mesh.castShadow = true;
          mesh.receiveShadow = true;
          // Activate vertex colours when the geometry carries COLOR_0 - common
          // in CC0 packs (Quaternius, Kenney, Kay Lousberg). GLTFLoader sets
          // this when the glTF material references the attribute, but some
          // exporters omit the declaration even when the data is present.
          if (mesh.geometry.attributes['color'] != null) {
            const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            for (const mat of mats) {
              const std = mat as THREE.MeshStandardMaterial;
              if (std && !std.vertexColors) { std.vertexColors = true; std.needsUpdate = true; }
            }
          }
        });
        mixer = new THREE.AnimationMixer(gltf.scene);
        setModel({ gltf, mixer });
        onClips?.(gltf.animations.map((a) => a.name));
      },
      undefined,
      (err) => console.warn('[library] gallery model failed to load:', err),
    );
    return () => {
      cancelled = true;
      mixer?.stopAllAction();
      actionRef.current = null;
      setModel(null);
      if (loadedScene) disposeProp(loadedScene);
    };
    // One load per URL; clip changes only swap actions below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    if (!model) return;
    if (!clip) {
      // Static mode: no clip requested, leave the mesh on its bind pose.
      const previous = actionRef.current;
      if (previous) previous.fadeOut(0.2);
      actionRef.current = null;
      return;
    }
    const raw = THREE.AnimationClip.findByName(model.gltf.animations, clip) ?? model.gltf.animations[0];
    if (!raw) return;
    const next = model.mixer.clipAction(raw);
    next.reset();
    next.setLoop(THREE.LoopRepeat, Infinity);
    next.timeScale = speed;
    next.fadeIn(0.2);
    next.play();
    const previous = actionRef.current;
    if (previous && previous !== next) previous.fadeOut(0.2);
    actionRef.current = next;
  }, [model, clip, speed]);

  useFrame((_, dt) => {
    if (!paused) model?.mixer.update(dt);
  });

  return model ? <primitive object={model.gltf.scene} /> : null;
}

/** The gallery scene chrome (matching AnimationGalleryStage) around any
 * animated library model. */
export function LibraryGalleryStage(props: LibraryGalleryProps) {
  const { background = '#11141b', style, className } = props;
  return (
    <div className={className} style={{ position: 'absolute', inset: 0, ...style }}>
      <Viewport shadows>
        <PerspectiveCamera makeDefault fov={45} near={0.1} far={100} position={[2.8, 1.7, 4.6]} />
        <color attach="background" args={[background]} />
        <hemisphereLight args={['#bcd2ff', '#202833', 1.1]} />
        <directionalLight
          color="#fff4e6"
          intensity={2.4}
          position={[3, 6, 4]}
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
        />
        <directionalLight color="#88aaff" intensity={0.8} position={[-4, 3, -3]} />
        <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
          <circleGeometry args={[6, 48]} />
          <meshStandardMaterial color="#1a1f29" roughness={0.95} metalness={0} />
        </mesh>
        <gridHelper args={[12, 24, '#2a3344', '#222a36']} />
        <LibraryRig {...props} />
        <OrbitControls target={[0, 1, 0]} enableDamping />
      </Viewport>
    </div>
  );
}
